import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "@hangar/web-components/core/Spinner";
import { Text } from "@hangar/web-components/core/Text";
import { useAuth } from "../../hooks/auth";
import { useAssets } from "../../hooks/utils";
import { useTranslation } from "../../hooks/i18n";
import { Button } from "@hangar/web-components/core/Button";
import {
  LoginContainer,
  LoginLoadingOverlay,
  LoadingContainer,
  PresentationSection,
  LogoLatam,
  LoginFormContainer,
  LoginCard,
  CardContent,
  FormHeader,
  LogoLatamLogin,
  ButtonContainer,
  FormFooter,
  ErrorContainer,
  BackToHomeButtonWrapper,
} from "./Login.styles";

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, login, error } = useAuth();
  const { assets } = useAssets();
  const { t } = useTranslation();

  interface LocationState {
    from?: { pathname: string };
  }

  const from = (location.state as LocationState)?.from?.pathname || "/dashboard";

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleLogin = async () => {
    await login(false);
  };

  return (
    <LoginContainer>
      {isLoading && (
        <LoginLoadingOverlay>
          <LoadingContainer>
            <Spinner />
            <Text variant="body-lg">{t("auth.login.verifying")}</Text>
          </LoadingContainer>
        </LoginLoadingOverlay>
      )}

      <PresentationSection $backgroundImage={assets.backgrounds.login}>
        <LogoLatam src={assets.logos.latamAirlines} alt="LATAM Airlines Logo" />
      </PresentationSection>

      <LoginFormContainer>
        <LoginCard>
          <CardContent>
            <FormHeader>
              <LogoLatamLogin src={assets.logos.latamAirlines} alt="LATAM Airlines Logo" />
              <Text variant="heading-lg" align="center">
                {t("auth.login.title")}
              </Text>
              <Text variant="body-md" align="center">
                {t("auth.login.subtitle")}
              </Text>
            </FormHeader>

            {error && (
              <ErrorContainer>
                <Text variant="body-md" align="center">
                  {error}
                </Text>
              </ErrorContainer>
            )}

            <ButtonContainer>
              <Button onClick={handleLogin} label={t("auth.login.button")} variant="primary" />
            </ButtonContainer>

            <FormFooter>
              <Text variant="body-sm" align="center">
                {t("auth.login.termsAndPrivacy")}
              </Text>
              <BackToHomeButtonWrapper>
                <Button onClick={() => navigate("/")} label={t("auth.login.backToHome")} variant="secondary" />
              </BackToHomeButtonWrapper>
            </FormFooter>
          </CardContent>
        </LoginCard>
      </LoginFormContainer>
    </LoginContainer>
  );
};
